// ─── The customer booking page ───────────────────────────────────────────────
//
// A stranger on a phone, who has never seen this before and will not read
// anything. @nulife/booking-ui's BookingJourney owns the steps; this file
// feeds it real availability and sends the result.
//
// ── What this page must never show ──────────────────────────────────────────
//
// Another customer's name, how busy the business is, or anything about a
// booking that is not the visitor's own. That is enforced at the API — the
// customer routes do not return those fields — and reinforced here by simply
// never asking a business route for anything.

import { useCallback, useState } from "react";
import {
  BookingJourney,
  type JourneyService, type JourneySlot, type JourneyDay,
} from "@nulife/booking-ui";
import * as api from "./api.js";
import { useAsync } from "@nulife/app-ui";
import { Message, Loading } from "@nulife/app-ui";

/**
 * How far ahead a customer may book.
 *
 * Ninety days again, because the journey now asks for a DAY before it asks for
 * a time. Nothing loads a quarter of times any more: the month calendar needs
 * only a day-by-day summary, and the time list needs one day.
 *
 * It was cut to fourteen on 2026-09-11 as a stopgap, when the slot step showed
 * every free time in the horizon in one list — eighteen hundred buttons. That
 * stopgap is what this replaces.
 */
const HORIZON_DAYS = 90;

export function Customer({ config }: { config: api.Config }) {
  // Times shown here are the SALON's clock, converted at the api boundary —
  // a customer booking from abroad must be told when to turn up in the
  // salon's terms, not their device's.
  const tz = config.timeZone;
  const [failure, setFailure] = useState<string | null>(null);

  const services = useAsync(() => api.getServices(), []);

  // ── What gets fetched, and when ───────────────────────────────────────────
  //
  // Two small requests instead of one enormous one. The journey asks for a DAY
  // before it asks for a time, so the month view needs only a day-by-day
  // summary and the time list needs a single day.
  //
  // Before the day step existed, this fetched every free time for every
  // service across the whole horizon up front — 1,856 slots, to render a list
  // nobody could use. Cutting the horizon to a fortnight made it 290, which
  // was less absurd and no more usable.
  const [monthWanted, setMonthWanted] = useState<
    { serviceId: string; year: number; monthIndex: number } | null>(null);
  const [dayWanted, setDayWanted] = useState<{ serviceId: string; day: Date } | null>(null);

  const monthDays = useAsync(async () => {
    if (!monthWanted) return [] as api.MonthDayFacts[];
    return api.getCustomerMonth(monthWanted.serviceId, monthWanted.year, monthWanted.monthIndex);
  }, [monthWanted?.serviceId, monthWanted?.year, monthWanted?.monthIndex]);

  const daySlots = useAsync(async () => {
    if (!dayWanted) return [] as api.Slot[];
    const from = new Date(dayWanted.day);
    from.setHours(0, 0, 0, 0);
    const to = new Date(from);
    to.setDate(to.getDate() + 1);
    return api.getAvailability(dayWanted.serviceId, from, to, tz);
  }, [dayWanted?.serviceId, dayWanted?.day?.getTime(), tz]);

  /**
   * The month's days, as wall-clock Dates.
   *
   * The API keys each day as "2027-03-01" in the SALON's zone, deliberately a
   * string rather than an instant — an instant would be re-read in the
   * viewer's zone and land on the wrong tile. Rebuilding it with the local
   * `Date` constructor gives a Date whose local fields are that calendar date,
   * which is what the calendar reads.
   */
  const daysFor = useCallback((service: JourneyService): JourneyDay[] => {
    // Guarded: a response for the PREVIOUS service must never be shown as
    // this one's. A two-hour colour has different free days from a trim.
    if (monthWanted?.serviceId !== service.id) return [];
    return (monthDays.data ?? []).map(d => {
      const [y, m, day] = d.date.split("-").map(Number);
      return { date: new Date(y!, m! - 1, day!), working: d.working, openSlots: d.openSlots };
    });
  }, [monthDays.data, monthWanted?.serviceId]);

  const slotsFor = useCallback((service: JourneyService, day: Date): JourneySlot[] => {
    if (dayWanted?.serviceId !== service.id) return [];
    if (!dayWanted.day || dayWanted.day.toDateString() !== day.toDateString()) return [];
    return daySlots.data ?? [];
  }, [daySlots.data, dayWanted?.serviceId, dayWanted?.day]);

  const onMonthChange = useCallback((service: JourneyService, year: number, monthIndex: number) => {
    setMonthWanted({ serviceId: service.id, year, monthIndex });
  }, []);

  const onPickDay = useCallback((service: JourneyService, day: Date) => {
    setDayWanted({ serviceId: service.id, day });
  }, []);

  if (services.loading && !services.data) return <Loading />;

  if (services.error) {
    // Said plainly, and NOT as an empty list. "No times available" would be a
    // claim about the business; this is a claim about the connection, and the
    // customer should try again rather than go elsewhere.
    return (
      <Message tone="bad">
        We could not load the salon's details just now. Please refresh, or call the salon.
      </Message>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {failure && <Message tone="bad" onDismiss={() => setFailure(null)}>{failure}</Message>}
      {(monthDays.error || daySlots.error) && (
        <Message tone="bad">
          We could not load the times just now. Please try again, or call the salon.
        </Message>
      )}

      <BookingJourney
        businessName={config.businessName}
        services={services.data ?? []}
        daysFor={daysFor}
        slotsFor={slotsFor}
        onMonthChange={onMonthChange}
        onPickDay={onPickDay}
        loading={monthDays.loading || daySlots.loading}
        mode={config.mode}
        onSubmit={async ({ service, slot, details, clientActionId }) => {
          try {
            const result = await api.requestBooking({
              serviceId: service.id,
              startsAt: slot.startsAt,
              // The instant the server offered, carried through the journey on
              // the slot object rather than re-derived from the wall clock.
              // On the morning the clocks go back a wall clock names two
              // instants, and a customer must be booked at the one they were
              // shown — not at whichever an inverse would have picked.
              instant: (slot as api.Slot).instant,
              // Generated once per tap by the journey and reused on retry.
              // Passed straight through — minting a new one here would make
              // every retry a new booking.
              clientActionId,
              details,
            }, tz);
            setFailure(null);
            return result;
          } catch (err) {
            if (err instanceof api.ApiError && err.slotTaken) {
              // Somebody took it while they were typing. Refresh THAT DAY
              // rather than leave a list holding a time that is gone.
              daySlots.reload();
              monthDays.reload();
              setFailure("Sorry — that time has just been taken. Here is what is still free.");
            } else {
              setFailure(err instanceof Error ? err.message : "Something went wrong.");
            }
            // Rethrown so the journey does NOT show a confirmation. This is the
            // whole reliability rule in one line: no success screen for
            // something that did not happen.
            throw err;
          }
        }}
      />
    </div>
  );
}
